import { ICategory, IkeywordsByType, TFormattedKeywordsByRetailer, TNestedRow, TRetailerRow } from 'types';
import { generateCategoriesKeywords, getRetailerKeywordsObjs } from './dataGenerators';

export const checkRetailers = (
    rowValues: TFormattedKeywordsByRetailer | TRetailerRow,
    setValue: (value: React.SetStateAction<TFormattedKeywordsByRetailer[]>) => void,
) => {
    if (rowValues.level === 'type') {
        setValue((prev) =>
            prev.map((type) => {
                if (type.id !== rowValues.id) return type;
                const newChecked = !type.checked;
                return {
                    ...type,
                    checked: newChecked,
                    subRows: type.subRows.map((retailer) => ({ ...retailer, checked: newChecked })),
                };
            }),
        );
    }

    if (rowValues.level === 'retailer') {
        setValue((prev) =>
            prev.map((type) => {
                if (type.id !== rowValues.parentId) return type;
                const subRows = type.subRows.map((retailer) =>
                    retailer.id === rowValues.id ? { ...retailer, checked: !retailer.checked } : retailer,
                );
                return { ...type, subRows, checked: subRows.every((retailer) => retailer.checked) };
            }),
        );
    }
};

export const checkCategories = (rowValues: ICategory, setValue: (value: React.SetStateAction<ICategory[]>) => void) => {
    if (rowValues.level === 'category') {
        setValue((prev) =>
            prev.map((category) => {
                if (category.id !== rowValues.id) return category;
                const newChecked = !category.checked;
                return {
                    ...category,
                    checked: newChecked,
                    subRows: category.subRows.map((subcategory) => ({
                        ...subcategory,
                        checked: newChecked,
                        subRows: subcategory.subRows.map((segment) => ({ ...segment, checked: newChecked })),
                    })),
                };
            }),
        );
    }
    if (rowValues.level === 'subcategory') {
        setValue((prev) =>
            prev.map((category) => {
                if (category.id !== rowValues.parentId?.id) return category;
                const subRows = category.subRows.map((subcategory) => {
                    if (subcategory.id !== rowValues.id) return subcategory;
                    const newChecked = !subcategory.checked;
                    return {
                        ...subcategory,
                        checked: newChecked,
                        subRows: subcategory.subRows.map((segment) => ({ ...segment, checked: newChecked })),
                    };
                });
                return { ...category, subRows, checked: subRows.every((subcategory) => subcategory.checked) };
            }),
        );
    }
    if (rowValues.level === 'segment') {
        setValue((prev) =>
            prev.map((category) => {
                if (category.id !== rowValues.parentId?.parentId?.id) return category;
                const subRows = category.subRows.map((subcategory) => {
                    if (subcategory.id !== rowValues.parentId?.id) return subcategory;
                    const segmentRows = subcategory.subRows.map((segment) =>
                        segment.id === rowValues.id ? { ...segment, checked: !segment.checked } : segment,
                    );
                    return {
                        ...subcategory,
                        subRows: segmentRows,
                        checked: segmentRows.every((segment) => segment.checked),
                    };
                });
                return { ...category, subRows, checked: subRows.every((subcategory) => subcategory.checked) };
            }),
        );
    }
};

export const updateKeywordsByType = (
    cat: ICategory[],
    setValue: (value: React.SetStateAction<IkeywordsByType[]>) => void,
): void => {
    const categoriesKeywords = generateCategoriesKeywords(cat, true);
    setValue((prev) => {
        return prev.map((e) => {
            return {
                ...e,
                keywords: e.keywords.map((keyword) => {
                    return { ...keyword, checked: categoriesKeywords.includes(Number(keyword.id)) };
                }),
            };
        });
    });
};

export const updateRetailerKeywordsByType = (
    retailers: TFormattedKeywordsByRetailer[],
    setValue: (value: React.SetStateAction<IkeywordsByType[]>) => void,
): void => {
    const retailerKeywords: number[] = [];

    retailers.forEach((retailer) => {
        retailerKeywords.push(...getRetailerKeywordsObjs(retailer).map((keyword) => keyword.id));
    });

    setValue((prev) => {
        return prev.map((e) => ({
            ...e,
            keywords: e.keywords.map((keyword) => {
                const newKeyword = { ...keyword };
                newKeyword.checked = retailerKeywords.includes(Number(newKeyword.id));
                return newKeyword;
            }),
        }));
    });
};

export const toggleCategory = (rowValues: ICategory, setValue: (value: React.SetStateAction<ICategory[]>) => void) => {
    if (rowValues.level === 'category') {
        setValue((prev) =>
            prev.map((category) =>
                category.id === rowValues.id ? { ...category, isExpanded: !category.isExpanded } : category,
            ),
        );
    }
    if (rowValues.level === 'subcategory') {
        setValue((prev) =>
            prev.map((category) => ({
                ...category,
                subRows: category.subRows.map((subcategory) =>
                    subcategory.id === rowValues.id
                        ? { ...subcategory, isExpanded: !subcategory.isExpanded }
                        : subcategory,
                ),
            })),
        );
    }
};

export const toggleRetailer = (
    rowValues: TFormattedKeywordsByRetailer,
    setValue: (value: React.SetStateAction<TFormattedKeywordsByRetailer[]>) => void,
) => {
    setValue((prev) =>
        prev.map((type) => (type.id === rowValues.id ? { ...type, isExpanded: !type.isExpanded } : type)),
    );
};

export const areChildrenChecked = (catData: TNestedRow[]): boolean => {
    return catData.some((item) => {
        if (item.subRows && item.subRows.length) {
            return areChildrenChecked(item.subRows);
        }
        return item.checked;
    });
};
